import type { RunMode } from "../run/intent.ts";
import { levelIds, type AudioSettings, type LevelId, type VolumeLevel } from "../audio/audio-settings.ts";
import { COMPASS_PLACES_DEFAULT, DEFAULT_FRAME_LIMIT, type FrameLimit, type InterfaceStep, type OfferedInvitation, type RenderDivisor, type Size } from "odyssey-shared";
import type { ChatSizeChoice } from "../chat/size.ts";
import type { ChatTextChoice } from "../chat/text-size.ts";
import { resolveBindings, type Bindings } from "../controls/bindings.ts";
import type { LayoutChoice } from "../controls/layout.ts";
import type { BarPagingChoice, OthersNameplates, OwnNameplate, ZoomStep } from "../settings.ts";
import type { ReducedMotionChoice } from "../reduced-motion.ts";
import { actionOf, changedSettings, levelSetting, type SettingsDraft, type SettingsValues } from "./settings-model.ts";

/**
 * Settings against the page (P1.9-T44; ADR-061's amendment "Settings in sections, and nothing applies until
 * Apply"): the page's settings as Settings reaches them, each putting a value in force and keeping it per viewer
 * exactly as it did when a change applied at once; the values in force, read once as Settings opens; Apply, which
 * calls the changed settings' setters; and Discard's part for sound, the one setting heard before it is applied.
 */

/**
 * The page's world zoom: the viewer's step, and a way to take a new one, which moves the world and is kept per
 * viewer (`WorldZoom`).
 */
export interface ZoomSetting {
  readonly step: ZoomStep;
  setStep(step: ZoomStep): void;
}

/**
 * The page's render resolution (ADR-093 decision 10): the divisor in force, the divisors the window offers and the
 * window's device size, which the row's note is worked out from; a way to take a new choice, which resizes the
 * canvas's backing store and is kept per device (`RenderResolution`); and a call whenever the window or the divisor
 * in force changes, so an open Settings weighs the offer again.
 */
export interface RenderSetting {
  readonly divisor: RenderDivisor;
  readonly offered: readonly RenderDivisor[];
  readonly device: Size;
  setDivisor(divisor: RenderDivisor): void;
  readonly interfaceStep: InterfaceStep;
  readonly mostScale: number;
  scaleAt(step: InterfaceStep): number;
  setInterfaceStep(step: InterfaceStep): void;
  subscribe(listener: () => void): () => void;
}

/**
 * The page's frame limit (ADR-093 decisions 40 to 45): the choice in force, the display's rate as measured (null
 * before it is first taken), which decides whether 120 is offered, a way to take a new choice, which is put in force
 * at once and kept per device (`FrameLimiter`), and a call whenever the display's rate or the choice changes, so an
 * open Settings weighs the offer again.
 */
export interface FrameLimitSetting {
  readonly limit: FrameLimit;
  readonly refresh: number | null;
  setLimit(limit: FrameLimit): void;
  subscribe(listener: () => void): () => void;
}

/**
 * The page's Nameplates choices: what other players' plates draw and whether the player's own draws, and a way
 * to take a new choice, which changes every plate and is kept per viewer (`NameplateSettings`).
 */
export interface NameplateSetting {
  readonly others: OthersNameplates;
  readonly own: OwnNameplate;
  setOthers(value: OthersNameplates): void;
  setOwn(value: OwnNameplate): void;
}

/**
 * The page's health numbers setting: whether the player card's health bar shows its value, and a way to change
 * it, which is kept per viewer (`HealthNumbersSetting`).
 */
export interface HealthNumbers {
  readonly show: boolean;
  setShow(show: boolean): void;
}

/**
 * The page's Show idle and AFK marks choice (P1.9-T45): whether other players' idle and AFK marks draw for this
 * viewer, and a way to change it, which is kept per viewer (`IdleMarksSetting`).
 */
export interface IdleMarks {
  readonly show: boolean;
  setShow(show: boolean): void;
}

/**
 * The page's Show other players' emotes choice (P2.19-T7, ADR-114 decision 5.8): whether other players' emoji are
 * drawn over them, and a way to change it, which is kept per device (`EmotesOthersSetting`).
 */
export interface EmotesOthers {
  readonly show: boolean;
  setShow(show: boolean): void;
}

/**
 * The page's Show coordinates choice (P1.9-T197): whether the line under the minimap's field says where the player is,
 * and a way to change it, kept per device (`CoordinatesSetting`).
 */
export interface ShowCoordinates {
  readonly show: boolean;
  setShow(show: boolean): void;
}

/**
 * The page's Places on the compass choice (P1.9-T189): how many of the nearest known places the compass band points to,
 * 0 for Off to 12, and a way to change it, kept per device (`CompassPlacesSetting`).
 */
export interface CompassPlaces {
  readonly count: number;
  setCount(count: number): void;
}

/**
 * The page's Auto loot choice (P2.8-T79, ADR-091's amendment for P2.8-T79, point 4): whether the page asks to pick up
 * the player's own ground items as they come in reach, and a way to change it, kept per device (`AutoLootSetting`).
 */
export interface AutoLoot {
  readonly on: boolean;
  setOn(on: boolean): void;
}

/**
 * The page's Show key numbers on the bar choice (P2.18-T15): whether the ability bar's slots draw their keys, and a way
 * to change it, which is kept per device (`BarKeysSetting`).
 */
export interface BarKeys {
  readonly show: boolean;
  setShow(show: boolean): void;
}

/**
 * The page's Lock the ability bar choice (ADR-121 decision 3.2, P2.8-T29): whether a drag from the bar needs Shift, and
 * a way to change it, which is kept per device.
 */
export interface BarLock {
  readonly locked: boolean;
  setLocked(locked: boolean): void;
}

/**
 * The page's Reset ability bar (ADR-121 decision 2.5, P2.8-T29): whether the player's bar can be reset now (arranged,
 * its session's bar arrived, not frozen), and the reset, which sends the default bar to the server.
 */
export interface BarReset {
  canReset(): boolean;
  reset(): boolean;
}

/**
 * The page's Ability bar choice (P1.9-T133): All 10 or Five at a time, and a way to change it, which the bar follows
 * at once and which is kept per device (`BarPagingSetting`).
 */
export interface BarPaging {
  readonly choice: BarPagingChoice;
  setChoice(choice: BarPagingChoice): void;
}

/**
 * The performance panel (ADR-087 decision 3): whether it shows, and a way to show or hide it, which is kept per
 * viewer (`PerfMonitor`), the same setting F9 toggles.
 */
export interface PerformanceToggle {
  readonly shown: boolean;
  setShown(shown: boolean): void;
}

/**
 * The page's reduced motion: the viewer's choice, and a way to take a new one, which every surface that honours
 * reduced motion reads and which is kept per viewer (`ReducedMotionSetting`).
 */
export interface MotionSetting {
  readonly choice: ReducedMotionChoice;
  setChoice(choice: ReducedMotionChoice): void;
}

/**
 * The page's sound (ADR-085 decision 11).
 */
export interface AudioSetting {
  readonly settings: AudioSettings;
  /** Hears a level at once without keeping it, as a slider moves or a mute changes. */
  previewLevel(id: LevelId, level: VolumeLevel): void;
  /** Hears and keeps a level. */
  setLevel(id: LevelId, level: VolumeLevel): void;
  /** Sets and keeps whether sound stops while the window is in the background. */
  setBackgroundMute(mute: boolean): void;
  /** Sets and keeps whether a whisper's alert sounds while the window is in the background and muted. */
  setWhisperBackground(through: boolean): void;
}

/**
 * The page's chat size (P1.9-T42, ADR-061's amendment "the chat panel resizes"): the choice the panel's size is, or
 * null for a size the grip left between the three, and the setter Apply calls.
 */
export interface ChatSizeToggle {
  readonly choice: ChatSizeChoice | null;
  setChoice(choice: ChatSizeChoice): void;
}

/**
 * The page's chat text size (P1.9-T224): the choice in force and the setter Apply calls, which the chat shows at once.
 */
export interface ChatTextToggle {
  readonly choice: ChatTextChoice;
  setChoice(choice: ChatTextChoice): void;
}

/**
 * The page's key table (P1.9-T50): each action's key in force, and a way to put a whole table in force, which every
 * key reader and prompt follows at once and which is kept per device (`KeyBindings`).
 */
export interface KeysSetting {
  readonly bindings: Bindings;
  set(bindings: Bindings): void;
  /** The Keyboard layout choice the caps are labelled by (P1.9-T174). */
  readonly layoutChoice: LayoutChoice;
  /**
   * Puts a Keyboard layout choice in force and keeps it.
   *
   * @param choice The choice.
   */
  setLayout(choice: LayoutChoice): void;
}

/**
 * The page's Run setting (ADR-116 decision 11): Hold or Toggle, and a way to change it, which the next input follows
 * and which is kept per device (`PageRun`).
 */
export interface RunSetting {
  readonly mode: RunMode;
  setMode(mode: RunMode): void;
}

/**
 * The page's Party invitations setting (ADR-101 decision 6, P2.13-T16): who may invite the character to a party, as the
 * session last knew it, and a way to change it, which asks the gateway and applies to the next invitation. Only a page
 * that plays an account session has one.
 */
export interface PartyInvitesSetting {
  readonly value: OfferedInvitation;
  set(value: OfferedInvitation): void;
}

/**
 * Every setting of the page Settings changes; `performance` only on a page that has the panel, and `render` only
 * on a page that has a render resolution.
 */
export interface SettingsPage {
  readonly render?: RenderSetting | undefined;
  readonly frameLimit?: FrameLimitSetting | undefined;
  readonly zoom: ZoomSetting;
  readonly nameplates: NameplateSetting;
  readonly healthNumbers: HealthNumbers;
  readonly idleMarks: IdleMarks;
  readonly emotesOthers: EmotesOthers;
  readonly coordinates?: ShowCoordinates;
  readonly compassPlaces?: CompassPlaces;
  readonly autoLoot?: AutoLoot;
  readonly barKeys: BarKeys;
  readonly barPaging: BarPaging;
  readonly chatSize: ChatSizeToggle;
  readonly motion: MotionSetting;
  readonly chatText: ChatTextToggle;
  readonly audio: AudioSetting;
  readonly keys: KeysSetting;
  readonly run: RunSetting;
  readonly barLock?: BarLock | undefined;
  readonly barReset?: BarReset | undefined;
  readonly partyInvites?: PartyInvitesSetting | undefined;
  readonly performance?: PerformanceToggle | undefined;
}

/**
 * Reads every value in force, as Settings opens. The levels are copied, so a preview that follows does not move
 * them.
 *
 * @param page The page's settings.
 * @returns The values; a page without the performance panel reads as not showing it, one without a render
 *   resolution as Full, and one without a frame limit as the default.
 */
export function readSettings(page: SettingsPage): SettingsValues {
  const audio = page.audio.settings;
  return {
    renderDivisor: page.render?.divisor ?? 1,
    frameLimit: page.frameLimit?.limit ?? DEFAULT_FRAME_LIMIT,
    zoom: page.zoom.step,
    performance: page.performance?.shown ?? false,
    othersNameplates: page.nameplates.others,
    ownNameplate: page.nameplates.own,
    healthNumbers: page.healthNumbers.show,
    idleMarks: page.idleMarks.show,
    emotesOthers: page.emotesOthers.show,
    coordinates: page.coordinates?.show ?? true,
    compassPlaces: page.compassPlaces?.count ?? COMPASS_PLACES_DEFAULT,
    autoLoot: page.autoLoot?.on ?? false,
    barKeys: page.barKeys.show,
    barPaging: page.barPaging.choice,
    chatSize: page.chatSize.choice ?? "custom",
    reducedMotion: page.motion.choice,
    chatText: page.chatText.choice,
    interfaceStep: page.render?.interfaceStep ?? 0,
    backgroundMute: audio.backgroundMute,
    whisperBackground: audio.whisperBackground,
    levels: Object.fromEntries(levelIds().map((id) => [id, { volume: audio.levels[id].volume, muted: audio.levels[id].muted }])) as Record<LevelId, VolumeLevel>,
    runMode: page.run.mode,
    barLocked: page.barLock?.locked ?? false,
    keyLayout: page.keys.layoutChoice,
    partyInvites: page.partyInvites?.value ?? "map",
    keys: page.keys.bindings,
  };
}

/**
 * Apply: puts every changed setting's pending value in force through its own setter, which keeps it, in row
 * order, and calls no setter of a setting that is not changed. The keys are one table: when any action's key changed
 * the whole table is set once, by Apply's rule for keys (`resolveBindings`), which swaps a clash.
 *
 * @param page The page's settings.
 * @param draft The draft to apply.
 */
export function applySettings(page: SettingsPage, draft: SettingsDraft): void {
  const pending = draft.pending;
  let keysSet = false;
  for (const id of changedSettings(draft)) {
    if (actionOf(id) !== null) {
      if (!keysSet) {
        keysSet = true;
        page.keys.set(resolveBindings(draft.applied.keys, pending.keys));
      }
      continue;
    }
    switch (id) {
      case "renderDivisor":
        page.render?.setDivisor(pending.renderDivisor);
        break;
      case "frameLimit":
        page.frameLimit?.setLimit(pending.frameLimit);
        break;
      case "zoom":
        page.zoom.setStep(pending.zoom);
        break;
      case "performance":
        page.performance?.setShown(pending.performance);
        break;
      case "othersNameplates":
        page.nameplates.setOthers(pending.othersNameplates);
        break;
      case "ownNameplate":
        page.nameplates.setOwn(pending.ownNameplate);
        break;
      case "healthNumbers":
        page.healthNumbers.setShow(pending.healthNumbers);
        break;
      case "idleMarks":
        page.idleMarks.setShow(pending.idleMarks);
        break;
      case "emotesOthers":
        page.emotesOthers.setShow(pending.emotesOthers);
        break;
      case "coordinates":
        page.coordinates?.setShow(pending.coordinates);
        break;
      case "compassPlaces":
        page.compassPlaces?.setCount(pending.compassPlaces);
        break;
      case "autoLoot":
        page.autoLoot?.setOn(pending.autoLoot);
        break;
      case "barKeys":
        page.barKeys.setShow(pending.barKeys);
        break;
      case "barPaging":
        page.barPaging.setChoice(pending.barPaging);
        break;
      case "chatSize":
        if (pending.chatSize !== "custom") {
          page.chatSize.setChoice(pending.chatSize);
        }
        break;
      case "reducedMotion":
        page.motion.setChoice(pending.reducedMotion);
        break;
      case "interfaceStep":
        page.render?.setInterfaceStep(pending.interfaceStep);
        break;
      case "chatText":
        page.chatText.setChoice(pending.chatText);
        break;
      case "backgroundMute":
        page.audio.setBackgroundMute(pending.backgroundMute);
        break;
      case "whisperBackground":
        page.audio.setWhisperBackground(pending.whisperBackground);
        break;
      case "barLocked":
        page.barLock?.setLocked(pending.barLocked);
        break;
      case "runMode":
        page.run.setMode(pending.runMode);
        break;
      case "keyLayout":
        page.keys.setLayout(pending.keyLayout);
        break;
      case "partyInvites":
        page.partyInvites?.set(pending.partyInvites);
        break;
      default: {
        const level = levelIds().find((candidate) => levelSetting(candidate) === id);
        if (level !== undefined) {
          page.audio.setLevel(level, pending.levels[level]);
        }
      }
    }
  }
}

/**
 * Lets every level that differs between two sets of values be heard at the second's, without keeping anything,
 * since a level is never kept until Apply.
 *
 * @param page The page's settings.
 * @param before The values heard now.
 * @param after The values to hear.
 */
export function previewLevels(page: SettingsPage, before: SettingsValues, after: SettingsValues): void {
  for (const id of levelIds()) {
    const from = before.levels[id];
    const to = after.levels[id];
    if (from.volume !== to.volume || from.muted !== to.muted) {
      page.audio.previewLevel(id, to);
    }
  }
}

/**
 * Returns every level to the one in force, whether or not the draft says it was heard at another, without keeping
 * anything: what Settings does as it leaves the page, where a level previewed in the same task as the leaving
 * may not have reached the draft yet.
 *
 * @param page The page's settings.
 * @param applied The values in force.
 */
export function restoreLevels(page: SettingsPage, applied: SettingsValues): void {
  for (const id of levelIds()) {
    page.audio.previewLevel(id, applied.levels[id]);
  }
}

/**
 * Discard's part for sound: returns every level that was heard at a pending value to the one in force.
 *
 * @param page The page's settings.
 * @param draft The draft being dropped.
 */
export function revertLevels(page: SettingsPage, draft: SettingsDraft): void {
  previewLevels(page, draft.pending, draft.applied);
}
